import React, { useEffect, useState, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from './../axiosInstance.js';
import { localizeArt } from './../localize.js';
import MyUserContext from './../myUserContext.js';
import ArtThumbnail from './ArtThumbnail.js';
import { Paper, Text } from '@mantine/core';

const LogbookHistory = () => {
  const [logbookItems, setLogbookItems] = useState(null);
  const [user, setUser] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const myUser = useContext(MyUserContext);
  const language = myUser ? myUser.myUserSettings.lang : 'ru';
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const userId = queryParams.get('userId');
  const navigate = useNavigate();

  useEffect(() => {
    let isActive = true;
    const fetchLogbookHistory = async () => {
      const baseParams = new URLSearchParams();
      if (userId) {
        baseParams.set('userId', userId);
      }

      const firstParams = new URLSearchParams(baseParams);
      firstParams.set('count', '10');

      try {
        const firstResponse = await axiosInstance.get(`/logbookHistory?${firstParams.toString()}`);
        if (!isActive) return;
        setLogbookItems(firstResponse.data);

        const secondParams = new URLSearchParams(baseParams);
        secondParams.set('count', '10000');
        secondParams.set('offset', '10');
        setIsLoadingMore(true);
        const secondResponse = await axiosInstance.get(`/logbookHistory?${secondParams.toString()}`);
        setIsLoadingMore(false);
        if (!isActive) return;
        if (Array.isArray(secondResponse.data) && secondResponse.data.length > 0) {
          setLogbookItems((prev) => (prev || []).concat(secondResponse.data));
        }
      } catch (error) {
        setIsLoadingMore(false);
        console.error('Failed to fetch logbook history:', error);
      }
    };

    const fetchUser = () => {
      if (userId) {
        axiosInstance
          .get(`/users/${userId}`)
          .then((response) => setUser(response.data))
          .catch((error) => console.error('Failed to fetch user:', error));
      }
    };

    fetchLogbookHistory();
    fetchUser();
    return () => { isActive = false; };
  }, [userId]);

  if (logbookItems === null) {
    return <div></div>;
  }

  // Group items by month
  const groupedByMonth = [];
  let currentGroup = null;
  for (const item of logbookItems) {
    const monthKey = new Date(item.logTime).getTime() === 0
      ? (language === 'ru' ? 'Давно' : 'Long time ago')
      : new Date(item.logTime).toLocaleString('default', { month: 'long', year: 'numeric' });

    if (!currentGroup || currentGroup.month !== monthKey) {
      currentGroup = { month: monthKey, items: [] };
      groupedByMonth.push(currentGroup);
    }
    currentGroup.items.push(item);
  }

  const totalCount = logbookItems.length;
  const movieCount = logbookItems.filter(i => i.art.category === 1).length;
  const bookCount = logbookItems.filter(i => i.art.category === 3).length;
  const gameCount = logbookItems.filter(i => i.art.category === 2).length;

  return (
    <div className="px-3 pb-6">
      <div className="items-center mt-4 mb-2">
        <img
          src="/back_icon.png"
          alt="Back"
          className="cursor-pointer w-6 h-6"
          style={{ filter: 'var(--icon-filter)' }}
          onClick={() => navigate(-1)}
        />
      </div>

      <div className="flex flex-col items-center">
        <h1 className="text-2xl font-bold mb-1">
          {language === 'ru' ? 'Журнал' : 'Logbook'}
        </h1>
        {user && (
          <div className="flex items-center gap-2 mb-2">
            <img src={user.avatarUrl} alt={user.userName} className="w-7 h-7 rounded-full" />
            <Text size="sm" c="dimmed">{user.userName}</Text>
          </div>
        )}
        {totalCount > 0 && (
          <Text size="sm" c="dimmed" className="mb-4">
            {[
              movieCount > 0 && `${movieCount} ${language === 'ru' ? (movieCount === 1 ? 'фильм' : 'фильмов') : (movieCount === 1 ? 'movie' : 'movies')}`,
              bookCount > 0 && `${bookCount} ${language === 'ru' ? (bookCount === 1 ? 'книга' : 'книг') : (bookCount === 1 ? 'book' : 'books')}`,
              gameCount > 0 && `${gameCount} ${language === 'ru' ? (gameCount === 1 ? 'игра' : 'игр') : (gameCount === 1 ? 'game' : 'games')}`,
            ].filter(Boolean).join(' \u00b7 ')}
          </Text>
        )}
      </div>

      {logbookItems.length === 0 && (
        <div className="flex justify-center text-center mt-8">
          <Text c="dimmed">{language === 'ru' ? 'Пока ничего не добавлено в журнал' : "Nothing added to the logbook yet"}</Text>
        </div>
      )}

      {isLoadingMore && (
        <div className="flex justify-center mb-4">
          <div className="w-full max-w-md">
            <div className="h-2 w-full overflow-hidden rounded-full" style={{ backgroundColor: 'var(--mantine-color-default)' }}>
              <div className="similars-progress-bar h-full w-1/3 rounded-full" style={{ backgroundColor: 'var(--mantine-color-default-border)' }}></div>
            </div>
          </div>
        </div>
      )}

      {groupedByMonth.map((group) => {
        const monthMovies = group.items.filter(i => i.art.category === 1).length;
        const monthBooks = group.items.filter(i => i.art.category === 3).length;
        const monthGames = group.items.filter(i => i.art.category === 2).length;
        const counts = [];
        if (monthMovies > 0) counts.push(String(monthMovies));
        if (monthBooks > 0) counts.push(String(monthBooks));
        if (monthGames > 0) counts.push(String(monthGames));

        return (
          <div key={group.month} className="mb-6">
            <div className="flex items-baseline gap-2 mb-2">
              <Text size="lg" fw={700} c="blue">{group.month}</Text>
              <Text size="sm" c="dimmed">{group.items.length}</Text>
            </div>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5">
              {group.items.map((item) => {
                const localized = localizeArt(item.art, language);
                const dayStr = new Date(item.logTime).getTime() === 0
                  ? ''
                  : new Date(item.logTime).toLocaleDateString(language, { day: 'numeric', month: 'short' });
                return (
                  <div
                    key={item.id || item.art.id + item.logTime}
                    className="cursor-pointer"
                    onClick={() => setSelectedItem(selectedItem?.id === item.id && selectedItem?.logTime === item.logTime ? null : item)}
                  >
                    <div className="relative">
                      <ArtThumbnail art={item.art} displayCheckmark={false} />
                      {item.finished && (
                        <div className="absolute bottom-1 right-1 rounded-full p-0.5" style={{ backgroundColor: 'var(--mantine-color-body)' }}>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" style={{ color: 'var(--mantine-color-green-text)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <Text size="xs" fw={500} className="mt-0.5 px-0.5 line-clamp-2 text-center leading-tight">
                      {localized.name}
                    </Text>
                    {dayStr && (
                      <Text size="xs" c="dimmed" className="text-center leading-tight">
                        {dayStr}
                      </Text>
                    )}
                    {item.note && (
                      <Text size="xs" c="dimmed" className="text-center leading-tight line-clamp-2 mt-0.5" style={{ fontStyle: 'italic' }}>
                        {item.note}
                      </Text>
                    )}
                  </div>
                );
              })}
            </div>
            {selectedItem && group.items.some(i => (i.id || i.art.id + i.logTime) === (selectedItem.id || selectedItem.art.id + selectedItem.logTime) && i.logTime === selectedItem.logTime) && (
              <Paper shadow="xs" radius="md" className="mt-2 p-3" style={{ backgroundColor: 'var(--app-surface-hover)' }}>
                <div className="flex items-center gap-3">
                  <Link to={`/arts/${selectedItem.art.id}`}>
                    <img src={selectedItem.art.thumbnailUrl} alt="" className="w-14 h-14 object-cover rounded-md" />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/arts/${selectedItem.art.id}`}>
                      <Text size="sm" fw={500} className="truncate">{localizeArt(selectedItem.art, language).name}</Text>
                    </Link>
                    {new Date(selectedItem.logTime).getTime() !== 0 && (
                      <Text size="xs" c="dimmed">
                        {new Date(selectedItem.logTime).toLocaleDateString(language, { day: '2-digit', month: 'short', year: 'numeric' })}
                      </Text>
                    )}
                    {selectedItem.note && (
                      <Text size="xs" c="dimmed" className="mt-1" style={{ fontStyle: 'italic' }}>
                        {selectedItem.note}
                      </Text>
                    )}
                  </div>
                  {selectedItem.artEventId && (
                    <Link to={`/artEvents/${selectedItem.artEventId}`}>
                      <img src="/chat_icon.png" className="w-5 h-5" style={{ filter: 'var(--icon-filter)' }} />
                    </Link>
                  )}
                </div>
              </Paper>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default LogbookHistory;
